/**********************************************************************
 *
 * Modified from https://github.com/FoundersFactory/react-speech-recognition.git
 * Original Author: James Brill <james.brill@foundersfactory.co>
 *
 **********************************************************************/


import React, { Component } from 'react'
import * as Sentry from '@sentry/browser';

export default function SpeechRecognition(options) {
  const SpeechRecognitionInner = function (WrappedComponent) {
    const BrowserSpeechRecognition =
      typeof window !== 'undefined' &&
      (window.SpeechRecognition ||
        window.webkitSpeechRecognition ||
        window.mozSpeechRecognition ||
        window.msSpeechRecognition ||
        window.oSpeechRecognition);
    const recognition = BrowserSpeechRecognition
      ? new BrowserSpeechRecognition()
      : null
    const browserSupportsSpeechRecognition = recognition !== null

    let pauseAfterDisconnect = false
    let interimTranscript = ''
    let finalTranscript = ''
    let recognizerIsStarting = '';
    let resetRequested = '';
    let stopRequested = '';
    let justStarted = false;
    const afterStartDeadTime = 10;
    let resettingTranscript = false;
    let listening
    if (
      options && options.autoStart === true
    ) {
      recognizerIsStarting = true;
      recognition.start()
      listening = true
    } else {
      listening = false
    }

    return class SpeechRecognitionContainer extends Component {
      constructor(props) {
        super(props)

        this.state = {
          interimTranscript,
          finalTranscript,
          listening: false
        }
        this.blockNewTranscript = false;
      }

      componentDidMount() {
        if (recognition) {
          recognition.continuous = true
          recognition.interimResults = true
          recognition.onresult = this.updateTranscript.bind(this)
          recognition.onend = this.onRecognitionDisconnect.bind(this)
          recognition.onstart = this.onStart.bind(this);
          recognition.onerror = this.onError.bind(this);
          this.setState({ listening })
        }
      }

      onStart(evt)
      {
        //console.log("SR IN ONSTART",evt, new Date().getTime());
        //Deferred calls to abort and stop to prevent a known (by us)
        // race condition if abort or stop is called right after start
        if(!resetRequested) pauseAfterDisconnect = false;
        if(resetRequested)
        {
          //console.log("SR IN ONSTART - ABORTING", new Date().getTime());
           recognition.abort();
           resetRequested = false;
        }
        if(stopRequested)
        {
          //console.log("SR IN ONSTART - STOPPING", new Date().getTime());
          setTimeout((function(){recognition.stop();}).bind(this),afterStartDeadTime);
           pauseAfterDisconnect = true;
           stopRequested = false;
        }

        recognizerIsStarting = false;
        justStarted = true;
        listening = true;
        this.setState({listening});
        resettingTranscript = false;
        setTimeout((function(){justStarted=false;}).bind(this),afterStartDeadTime);
        //console.log("SR OUT OF ONSTART",pauseAfterDisconnect,new Date().getTime());
      }

      disconnect(disconnectType) {
        if (recognition) {
          switch (disconnectType) {
            case 'ABORT':
            //console.log("SR ABORT REQUESTED AND EXECUTED",new Date().getTime());
              pauseAfterDisconnect = true
              recognition.abort()
              break
            case 'RESET':
              pauseAfterDisconnect = false
              if(!recognizerIsStarting)
              {
                //console.log("SR RESET EXECUTED",new Date().getTime());
                //console.log(new Error().stack);
                recognition.abort()
              }
              else
              {
                //console.log("SR RESET REQUESTED",new Date().getTime());
                resetRequested = true;
              }
              break
            case 'STOP':
            default:
              pauseAfterDisconnect = true;
              if(!recognizerIsStarting)
              {
                //console.log("SR STOP EXECUTED",new Date().getTime());
                //console.log(new Error().stack);
                if(justStarted)
                {
                  setTimeout((function(){
                    //console.log("DEFERRED STOP");
                    recognition.stop();
                  }).bind(this),afterStartDeadTime);
                }
                else
                {
                  recognition.stop();
                }

              }
              else
              {
                //console.log("SR STOP REQUESTED",new Date().getTime());
                stopRequested = true;
              }
          }
        }
      }

      onError(err)
      {
        //console.log("SR ERROR:",err.error,new Date().getTime());
      }

      onRecognitionDisconnect(evt) {
        //console.log("IN DISCONNECT, PAUSE=",pauseAfterDisconnect,new Date().getTime());
        //console.log(evt);
        listening = false
        resettingTranscript = false;
        if (pauseAfterDisconnect) {
          this.setState({ listening })
        } else {
          this.startListening()
        }
        pauseAfterDisconnect = false

        //console.log("OUT OF DISCONNECT");
      }

      updateTranscript(event) {
        if(this.blockNewTranscript || resettingTranscript){
          return;
        }
        interimTranscript = ''
        for (let i = event.resultIndex; i < event.results.length; ++i) {
          if (event.results[i].isFinal) {
            finalTranscript = this.concatTranscripts(
              finalTranscript,
              event.results[i][0].transcript
            )
          } else {
            interimTranscript = this.concatTranscripts(
              interimTranscript,
              event.results[i][0].transcript
            )
          }
        }
        this.blockNewTranscript = true;
        this.setState(
          { finalTranscript, interimTranscript },
          () => {this.blockNewTranscript = false}
        );
      }

      concatTranscripts(...transcriptParts) {
        return transcriptParts.map(t => t.trim()).join(' ').trim()
      }

      resetTranscript (transcript = '') {
        interimTranscript = '';
        finalTranscript = '';
        resettingTranscript = true;
        if(listening){
          this.disconnect('RESET');
        }else{
          this.disconnect('ABORT');
        }
        this.setState({ interimTranscript, finalTranscript });
      }

      startListening () {
        pauseAfterDisconnect = false;
        if (recognition && !listening) {
          try {
            recognizerIsStarting = true;
            recognition.start()
          } catch (DOMException) {
            // Tried to start recognition after it has already started - safe to swallow this error
            console.log("SR STARTED WHEN ALREADY RUNNING",DOMException.name,DOMException.message,new Date().getTime()); //Leaving this here - indication that we have an issue
            //###Sentry.captureException(DOMException);
            //console.log(new Error().stack);
            recognizerIsStarting = false;
          }
        }
      }

      abortListening () {
        pauseAfterDisconnect = true;
        if(listening){
          this.disconnect('ABORT')
        }
      }

      stopListening () {
        pauseAfterDisconnect = true;
        if(this.state.listening){
          this.disconnect('STOP')
        }
      }

      setOnSpeechStart(onspeechstart){      //TODO: consider renaming this
        recognition.onspeechstart = onspeechstart;
      }

      setGrammar(corpus){
        if(!corpus){
          return;
        }
        let grammar = "#JSGF V1.0; grammar corpusWords; public <corpusWords> = ";
        let splitCorpus = corpus.replace(/[.,\/#!$%\^&\*;:{}=\-_`~()""?!]/g,"").toLowerCase().split(' ');

        for(let i = 0; i < splitCorpus.length; i++){
          let word = splitCorpus[i];
          grammar += word;
          if(i < splitCorpus.length - 1){
            grammar += " | ";
          }
        }
      }

      setLanguage(locale){
        if(locale === 'es-mx'){
          recognition.lang = 'es-419'
        }else{
          recognition.lang = 'en-US'
        }
        console.log("SET language to:",recognition.lang)
      }

      render() {
        const transcript = this.concatTranscripts(
          finalTranscript,
          interimTranscript
        )

        return (
          <WrappedComponent
            resetTranscript={this.resetTranscript.bind(this)}
            startListening={this.startListening.bind(this)}
            abortListening={this.abortListening.bind(this)}
            stopListening={this.stopListening.bind(this)}
            setOnSpeechStart={this.setOnSpeechStart.bind(this)}
            setGrammar={this.setGrammar.bind(this)}
            setLanguage={this.setLanguage.bind(this)}
            transcript={transcript}
            recognition={recognition}
            browserSupportsSpeechRecognition={browserSupportsSpeechRecognition}
            {...this.state}
            {...this.props} />
        )
      }
    }
  }

  if (typeof options === 'function') {
    return SpeechRecognitionInner(options)
  } else {
    return SpeechRecognitionInner
  }
}
